export const SESSION_EXPIRES_IN_HEADER = 'X-Session-Expires-In';
export const SESSION_WARNING_LEAD_HEADER = 'X-Session-Warning-Lead';

export type SessionExpiry = {
  expiresAt: number;
  warningLeadMs: number;
};

// Browsers run a timer longer than about 24.8 days at once, which would show the
// warning and check the session straight away. The server's lifetimes are hours.
const MAX_EXPIRES_IN_SECONDS = 24 * 24 * 60 * 60;

let latestSessionExpiry: SessionExpiry | null = null;
const listeners = new Set<() => void>();

function parseSeconds(value: string | null) {
  if (value === null || !/^\d+$/.test(value)) {
    return null;
  }
  const seconds = Number(value);
  return seconds > 0 && seconds <= MAX_EXPIRES_IN_SECONDS ? seconds : null;
}

export function getSessionExpiry(): SessionExpiry | null {
  return latestSessionExpiry;
}

// The latest response wins, even when it ends the session sooner than before.
export function recordSessionExpiry(headers: Headers) {
  const expiresInSeconds = parseSeconds(headers.get(SESSION_EXPIRES_IN_HEADER));
  const warningLeadSeconds = parseSeconds(
    headers.get(SESSION_WARNING_LEAD_HEADER),
  );
  if (expiresInSeconds === null || warningLeadSeconds === null) {
    return;
  }

  latestSessionExpiry = {
    expiresAt: Date.now() + expiresInSeconds * 1000,
    warningLeadMs: warningLeadSeconds * 1000,
  };
  listeners.forEach((listener) => listener());
}

// An error still renews the session, but its handler may be deciding whether
// the session expired, and a new deadline would cancel that decision. Skipping it
// can only make the countdown end early, and the check at zero corrects that.
export function recordSessionExpiryFromResponse(response: Response) {
  // Every page's requests come through here, so a fetch stand-in without
  // headers must still get its response.
  if (response.ok && response.headers) {
    recordSessionExpiry(response.headers);
  }
}

export function subscribeToSessionExpiry(onChange: () => void) {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

export function resetSessionExpiryForTests() {
  latestSessionExpiry = null;
}
